import { OCCURRENCE_CHAT_RETENTION_DAYS, announcementReplyState } from "@edu/shared";
import { dayCountText } from "../../lib/plurals.ts";
import type { ApiErrorCode } from "../../lib/api.ts";

/**
 * Слова чата занятия и разбор состояний одного сообщения: то общее, что читают
 * все три места, где чат показывается.
 *
 * Мест сегодня три: панель комнаты (`features/room/RoomChat.tsx`), всплывающая
 * карточка занятия и строка списка уроков (`features/schedule/EntryChat.tsx`).
 * Проект целиком в `docs/room-chat-design.md`, тексты это его раздел 13.
 *
 * ⚠️ **Здесь лежит только то, что у трёх мест ОДИНАКОВО.** Пустая лента у них
 * разная (в комнате говорится про занятие, в карточке про собеседника, у
 * ученика и преподавателя слова свои), поэтому её слова живут по местам. А
 * надгробие, отказ по частоте и закрытое окно письма обязаны читаться одними и
 * теми же словами везде: одно и то же событие, прочитанное как два разных, это
 * поломка, которую человек чинит перезагрузкой.
 *
 * **Зачем чистые функции, а не разбор состояния по месту.** Тестов на
 * компоненты в проекте нет и не планируется (раздел 9 CLAUDE.md), поэтому всё,
 * что можно назвать словом «решение», выносится сюда и закрывается тестами.
 *
 * ⚠️ **`ApiErrorCode` импортируется ТИПОМ.** Импорт значением утащил бы сюда
 * весь `lib/api.ts` вместе с `runtime-config`, а этот модуль обязан открываться
 * в голом Node: его читают тесты. То же правило и по той же причине стоит в
 * `features/announcements/announcement-text.ts`.
 */

/**
 * Полное имя чата на экране. ⚠️ **Слово одно на все места, где чат называется
 * целиком**: кнопка в доке комнаты, точка входа в ветке без медиасервера,
 * предупреждение группы, подпись области в карточке занятия. Два похожих
 * названия рядом читатель разбирает сам, а разбирать он не обязан.
 *
 * ⚠️ ЗАГОЛОВОК внутри карточки занятия другой и короче («Чат»,
 * `features/schedule/entry-chat.ts`), и это не разнобой: слово «занятия» внутри
 * карточки занятия указывает на то, что и так рядом (раздел 13 проекта).
 */
export const CHAT_LABEL = "Чат занятия";

/**
 * Постоянная подпись у поля в групповом чате.
 *
 * ⚠️ **Обязательна, и это не дубль пустого состояния**: пустое состояние
 * исчезает после первого же сообщения, а единственное предупреждение о том, кто
 * читает написанное, исчезать не должно.
 */
export const GROUP_FORM_NOTE = "Читают все в группе";

/**
 * Почему на месте формы ничего нет.
 *
 * ⚠️ **Строка одна на два случая, и это правило, а не экономия** (тот же приём,
 * что у `REPLIES_CLOSED_TEXT` в переписке кабинета): её читает и тот, у кого
 * формы нет заранее (`canWrite` приехал ложью), и тот, кто успел нажать до
 * закрытия окна и получил отказ. Разные слова означали бы, что одно и то же
 * событие человек читает как два разных.
 *
 * ⚠️ Второго предложения («прошлое остаётся») здесь нет намеренно: лента с этими
 * сообщениями стоит прямо над строкой, и рассказывать словами про то, что видно
 * глазами, значит указывать пальцем на соседний элемент.
 */
export const CHAT_CLOSED_TEXT = "Написать уже нельзя: с занятия прошло больше суток.";

/** Лента не приехала. Кнопку повтора рисует общий `LoadFailed`. */
export const CHAT_FAILED_TEXT = "Чат не загрузился";

export const CHAT_DELETED_TEXT = "Сообщение удалено";

/** Подпись значка «убрать» у сообщения. Одно слово на все три места. */
export const CHAT_REMOVE_LABEL = "Убрать сообщение";

/**
 * Тело стёрлось по сроку хранения.
 *
 * ⚠️ **Число берётся из НАШЕЙ константы, а не у соседа.** Сегодня срок чата и
 * срок объявления это одно и то же число, и списать текст у объявлений было бы
 * соблазнительно; но константы две (`OCCURRENCE_CHAT_RETENTION_DAYS` и
 * `ANNOUNCEMENT_RETENTION_DAYS`), и в тот день, когда одну из них подвинут,
 * заимствованный текст начнёт врать молча.
 */
export function chatErasedText(): string {
  return `Текст больше не хранится: прошло ${dayCountText(OCCURRENCE_CHAT_RETENTION_DAYS)}`;
}

/**
 * Текст сообщения и то, показывать ли его приглушённо.
 *
 * ⚠️ **Состояний три, а полей под них два**, и разбирает пару готовая
 * `announcementReplyState` из контрактов: «удалено человеком» и «стёрто по
 * сроку» это разные события с разными словами, а второе написание разбора одной
 * и той же пары однажды разойдётся с первым.
 */
export function chatMessageText(msg: { body: string | null; deletedAt: number | null }): {
  text: string;
  dim: boolean;
  /** Надгробие: убирать такое второй раз не из чего, поэтому кнопки у него нет. */
  gone: boolean;
} {
  const state = announcementReplyState(msg);
  if (state === "deleted") return { text: CHAT_DELETED_TEXT, dim: true, gone: true };
  if (state === "erased") return { text: chatErasedText(), dim: true, gone: true };
  return { text: msg.body ?? "", dim: false, gone: false };
}

/**
 * Отказ словами.
 *
 * ⚠️ **Лимит частоты вешается на СТАТУС, а не на код** (раздел 7 проекта): поле
 * `error` в ответе лимитёра наше, а не его, поэтому до словаря кодов такой отказ
 * доезжает как «мимо словаря». Проверять статус надо ДО кода, иначе строка про
 * частоту не покажется никогда.
 *
 * `fallback` это `message` у `ApiError`: отказы, которых здесь нет, обязаны
 * доезжать как есть, а не превращаться в «что-то пошло не так».
 */
export function chatErrorText(status: number, code: ApiErrorCode, fallback: string): string {
  if (status === 429) return "Слишком часто, повторите через минуту";
  return CHAT_ERRORS[code] ?? fallback;
}

const CHAT_ERRORS: Readonly<Partial<Record<ApiErrorCode, string>>> = {
  occurrence_chat_closed: CHAT_CLOSED_TEXT,
  occurrence_cancelled: "Занятие отменено: написать уже нельзя.",
  /* Промах датой возможен только из расписания, и означает он одно: неделю
     поправили в соседней вкладке. Лечится перечитыванием, а не повтором */
  occurrence_not_scheduled: "Занятия в этот день больше нет. Обновите страницу.",
  occurrence_chat_not_found: "Этого чата больше нет. Обновите страницу.",
  occurrence_chat_message_not_found: "Этого сообщения больше нет. Обновите страницу.",
};

/* ─────────────────────────────── решения ───────────────────────────────── */

/** Что рисовать на месте ленты. */
export type ChatBody = "loading" | "failed" | "empty" | "list";

/**
 * Ожидание, отказ, пустота или лента.
 *
 * ⚠️ **Отказ и ожидание не показываются вместе** (правило `lib/loading.tsx`):
 * ждать нечего, если уже отказано. Поэтому решение одно на четыре исхода, а не
 * два независимых булевых поля, из которых экран собирает картину сам.
 *
 * ⚠️ **Отказ виден ТОЛЬКО когда показать вместо него нечего.** Опрос ленты
 * рвётся штатно (раздел 3 CLAUDE.md), и красная строка поверх уже приехавших
 * сообщений сообщала бы о поломке, которой для дела человека нет: он читает то,
 * что приехало, а следующий заход всё равно случится сам.
 */
export function chatBody(state: { loaded: boolean; failed: boolean; empty: boolean }): ChatBody {
  if (!state.loaded) return state.failed ? "failed" : "loading";
  return state.empty ? "empty" : "list";
}
